import { company } from '@/src/components/seo/StructuredData';

export const countries = {
  193: {
    // السعودية
    en: 'Saudi Arabia',
    ar: 'المملكة العربية السعودية',
  },
  17: {
    // البحرين
    en: 'Bahrain',
    ar: 'البحرين',
  },
  116: {
    // الكويت
    en: 'Kuwait',
    ar: 'الكويت',
  },
} as const;

type Locale = 'en' | 'ar';

export function getStoreDescription({
  description,
  storeName,
  countryId,
  locale,
}: {
  description?: string;
  storeName: string;
  countryId: keyof typeof countries;
  locale: Locale;
}): string {
  const minLength = 60;

  if (description && description.length >= minLength) {
    return description;
  }

  const countryName = countries[countryId]?.[locale] || '';

  const templates = {
    en: `Discover ${storeName} , offering trusted services and a great customer experience in ${countryName}.`,
    ar: `اكتشف ${storeName} ، حيث نقدم خدمات موثوقة وتجربة عملاء مميزة في ${countryName}.`,
  };

  return templates[locale];
}

export function cleanDescription(text: string): string {
  return text
    .replace(/<[^>]*>/g, '')
    .replace(/\r\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 155);
}

// دالة لإنشاء Canonical URL
export function getCanonicalUrl(path: string, locale?: string): string {
  const baseUrl = company.url;
  const localePath = locale ? `/${locale}` : '';
  return `${baseUrl}${localePath}${path}`;
}

// دالة لإنشاء Hreflang Tags
export function generateHreflangTags(path: string) {
  const baseUrl = company.url;
  const locales = ['en', 'ar'];

  return locales.map((locale) => ({
    rel: 'alternate',
    hreflang: locale === 'ar' ? 'ar-SA' : 'en-US',
    href: `${baseUrl}/${locale}${path}`,
  }));
}

// دالة لإنشاء JSON-LD للـ FAQ
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function generateDigitalCardDynamicTitle(
  productName: string,
  price: number,
  currency: string,
  locale: string,
): string {
  const isArabic = locale === 'ar';

  if (isArabic) {
    return `بطاقة هدايا ${productName} - ${price} ${currency} | اشترِ الآن`;
  }

  return `${productName} Gift Card - ${price} ${currency} | Buy Now`;
}

export function generateDigitalCardDynamicDescription(
  productName: string,
  storeName: string,
  locale: string,
): string {
  const isArabic = locale === 'ar';

  if (isArabic) {
    return `احصل على بطاقة هدايا ${productName} الرقمية من ${storeName}. تسليم فوري عبر البريد الإلكتروني. أفضل الأسعار والعروض في المملكة العربية السعودية.`;
  }

  return `Get ${productName} digital gift card from ${storeName}. Instant delivery via email. Best prices and offers in Saudi Arabia. Shop now!`;
}

// دالة لتتبع PageView (Google Analytics)
export function trackProductView(product: any) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'view_item', {
      currency: product.currencyCode,
      value: product.price,
      items: [
        {
          item_id: product.providerBrandCode,
          item_name: product.englishName,
          price: product.price,
          item_category: product.categories[0]?.englishName,
          quantity: 1,
        },
      ],
    });
  }
}

export const safeProductId = (integrationId: string) => {
  return integrationId.replace(/\./g, '-_dot_-');
};
